import getGraphQlClient from '~graphql/getGraphQlClient';
import { onErrorShowMessage } from '~src/errors';
import { CREATE_ALLEGATI } from '~graphql/queries/createAllegati';
import { isLoggedUserGlobalEditor } from '~src/user';
import {
  handleTabAttachments,
  displaySidebarAttachments,
  fetchEditorAttachments,
} from '~src/sidebarLegacy';
import { handleTabImagesAI } from '~src/sidebar/index';
import { fetchAiCredits } from '~graphql/controllers';
import { pollForCreditChange } from '~core/aiCredits';
import { getUser } from '~src/user';
import slugify from 'slugify';
import { getState } from '~src/state';

export const onErrorAttachment = (file, error, response) => {
  console.error(file, error, response);
  onErrorShowMessage();
};

export const onCompleteAttachment = async (results) => {
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    throw new Error('Global Editor Cannot upload');
  }

  const graphQlClient = await getGraphQlClient();

  const promises = [];

  for (const result of results.successful) {
    const createAllegatiInput = {
      name: slugify(result.name),
      filename: new URL(result.uploadURL).pathname.substring(1),
      url: `${process.env.EDGE_RESIZER}${new URL(result.uploadURL).pathname}`,
      mimeType: result.type,
      canvasId: window.editorLoader.canvasId,
      size_bytes: result.size,
    };

    let props;

    if (result.meta && result.meta.customAttachmentProps) {
      props = result.meta.customAttachmentProps;
    } else if (result.customAttachmentProps) {
      props = result.customAttachmentProps;
    }

    if (props) {
      if (props.isRemovebg) {
        createAllegatiInput.isRemovebg = props.isRemovebg;
      }
      if (props.isBlurBg) {
        createAllegatiInput.isBlurBg = props.isBlurBg;
      }
      if (props.removebgRawImage) {
        createAllegatiInput.removebgRawImage = props.removebgRawImage;
      }
      if (props.isImageEnhancement) {
        createAllegatiInput.isImageEnhancement = props.isImageEnhancement;
      }
      if (props.enhancementRawImage) {
        createAllegatiInput.enhancementRawImage = props.enhancementRawImage;
      }
      if (props.isMagicEraser) {
        createAllegatiInput.isMagicEraser = props.isMagicEraser;
      }
      if (props.magicEraserRawImage) {
        createAllegatiInput.magicEraserRawImage = props.magicEraserRawImage;
      }
      if (props.magicEraserMask) {
        createAllegatiInput.magicEraserMask = props.magicEraserMask;
      }
      if (props.isChangeClothes) {
        createAllegatiInput.isChangeClothes = props.isChangeClothes;
      }
      if (props.changeClothesRawImage) {
        createAllegatiInput.changeClothesRawImage = props.changeClothesRawImage;
      }
      if (props.changeClothesMask) {
        createAllegatiInput.changeClothesMask = props.changeClothesMask;
      }
      if (props.changeClothesItem) {
        createAllegatiInput.changeClothesItem = props.changeClothesItem;
      }
      if (props.isSmile) {
        createAllegatiInput.isSmile = props.isSmile;
      }
      if (props.smileRawImage) {
        createAllegatiInput.smileRawImage = props.smileRawImage;
      }
      if (props.isColorizeBw) {
        createAllegatiInput.isColorizeBw = props.isColorizeBw;
      }
      if (props.colorizeBwRawImage) {
        createAllegatiInput.colorizeBwRawImage = props.colorizeBwRawImage;
      }
      if (props.isExpand) {
        createAllegatiInput.isExpand = props.isExpand;
      }
      if (props.expandRawImage) {
        createAllegatiInput.expandRawImage = props.expandRawImage;
      }
      if (props.isAutoColor) {
        createAllegatiInput.isAutoColor = props.isAutoColor;
      }
      if (props.rawImage) {
        createAllegatiInput.rawImage = props.rawImage;
      }
      if (props.isAiEdit) {
        createAllegatiInput.isAiEdit = props.isAiEdit;
      }
      if (props.aiEditPrompt) {
        createAllegatiInput.aiEditPrompt = props.aiEditPrompt;
      }
      if (props.aiEditRawImage) {
        createAllegatiInput.aiEditRawImage = props.aiEditRawImage;
      }
    }

    const servizio = getState('selected.servizio');
    if (servizio && servizio.objectId) {
      createAllegatiInput.defuntoId = servizio.objectId;
    }

    const createAttachmentPromise = graphQlClient.mutate({
      mutation: CREATE_ALLEGATI(createAllegatiInput),
    });

    promises.push(createAttachmentPromise);
  }

  if (results.failed && results.failed.length > 0) {
    onErrorShowMessage();
  }

  if (promises.length > 0) {
    try {
      await Promise.all(promises);

      const { attachments } = await fetchEditorAttachments(true);
      window.editorConfig.sidebarAttachments = attachments;
      displaySidebarAttachments();
      handleTabAttachments();

      // Check if any attachment is AI-generated (which consumes credits)
      const hasAiAttachment = results.successful.some((result) => {
        const props =
          result.meta?.customAttachmentProps || result.customAttachmentProps;
        return (
          props &&
          (props.isRemovebg ||
            props.isBlurBg ||
            props.isImageEnhancement ||
            props.isMagicEraser ||
            props.isChangeClothes ||
            props.isSmile ||
            props.isColorizeBw ||
            props.isExpand ||
            props.isAiEdit)
        );
      });

      // Check if org has image enhancement exception
      let hasImageEnhancementException = false;
      const hasImageEnhancementAttachment = results.successful.some(
        (result) => {
          const props =
            result.meta?.customAttachmentProps || result.customAttachmentProps;
          return props && props.isImageEnhancement;
        }
      );

      if (hasImageEnhancementAttachment) {
        const userData = await getUser();
        if (
          userData &&
          userData.user &&
          userData.user.org_pointer &&
          userData.user.org_pointer.createdAt
        ) {
          const orgCreatedAt = new Date(userData.user.org_pointer.createdAt);
          const exceptionCutoffDate = new Date('2025-04-01');
          hasImageEnhancementException = orgCreatedAt < exceptionCutoffDate;
        }
      }

      if (hasAiAttachment) {
        // Immediate refresh
        handleTabImagesAI();

        // Skip polling if this is an image enhancement feature with org exception
        if (!hasImageEnhancementException) {
          // Get initial credit balance
          let initialCredits = 0;
          try {
            const creditsData = await fetchAiCredits();
            if (creditsData && creditsData.edges && creditsData.edges[0]) {
              initialCredits =
                creditsData.edges[0].freeCreditsAmount +
                creditsData.edges[0].paidCreditsAmount;
            }
          } catch (error) {
            console.error('Error fetching initial credits:', error);
          }

          // Poll for credit changes after Lambda processing
          pollForCreditChange(initialCredits);
        }
      } else {
        handleTabImagesAI();
      }
    } catch (err) {
      console.error(err);
      onErrorShowMessage();
    }
  }
};
